"use client";

import React from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { motion } from 'framer-motion';
import { MessageSquare } from 'lucide-react';
import { LandingHeader } from '@/app/components/LandingHeader';
import { Footer } from '@/app/components/Footer';
import { CallbackForm } from '@/app/components/form/CallbackForm';
import { cn } from '@/lib/utils';

export default function GetInTouchPage() {
  const [isSupportOnline, setIsSupportOnline] = React.useState(true);

  React.useEffect(() => {
    const checkSupportStatus = () => {
      const now = new Date();
      const day = now.getUTCDay();
      const hour = now.getUTCHours();
      const isWeekday = day >= 1 && day <= 5;
      const isWorkingHours = hour >= 9 && hour < 18;
      setIsSupportOnline(isWeekday && isWorkingHours);
    };

    checkSupportStatus();
    const interval = setInterval(checkSupportStatus, 60000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="flex flex-col min-h-screen bg-background text-foreground font-body overflow-x-hidden">
      <LandingHeader transparent />
      
      <main className="flex-1 pt-32 pb-24 relative">
        {/* Background Elements */}
        <div className="absolute inset-0 z-0 opacity-[0.03] pointer-events-none">
          <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_1px_1px,#1a1a1a_1px,transparent_0)] bg-size-[40px_40px]" />
        </div>

        <div className="container mx-auto px-6 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-start">
            
            {/* Left Column: Info */}
            <motion.div 
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="lg:sticky lg:top-32"
            >
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal/10 text-teal text-sm font-medium mb-6">
                <MessageSquare className="w-4 h-4" />
                <span>We're here to help</span>
              </div>
              
              <h1 className="font-display text-5xl md:text-6xl lg:text-7xl mb-6 leading-[0.95]">
                Get in <span className="text-teal">Touch</span>
              </h1>
              
              <p className="text-xl text-muted-foreground font-light mb-12 max-w-lg leading-relaxed">
                Have questions about our service or need help with your Will? We'd be happy to help.
              </p>

              <div className="space-y-8 mb-12">
                <div className="flex items-start gap-4">
                  <div>
                    <h3 className="font-bold text-lg mb-1">Email Us</h3>
                    <a href="mailto:hello@willdone.co.uk" className="text-xl font-medium hover:text-teal transition-colors underline decoration-1 underline-offset-4">
                      hello@willdone.co.uk
                    </a>
                  </div>
                </div>
              </div>

              {/* Team Section */}
              <div className="bg-white border border-foreground/10 p-6 rounded-2xl shadow-sm">
                <div className="flex items-center gap-4 mb-4">
                  <div className="flex -space-x-3">
                    {[
                      { src: "/draftwill imagery/team/p1.jpeg", alt: "Team Member 1" },
                      { src: "/draftwill imagery/team/p2.jpeg", alt: "Team Member 2" },
                      { src: "/draftwill imagery/team/p3.jpeg", alt: "Team Member 3" }
                    ].map((p, i) => (
                      <div key={i} className="w-10 h-10 rounded-full border-2 border-white bg-muted overflow-hidden relative">
                         <Image 
                           src={p.src} 
                           alt={p.alt} 
                           fill 
                           sizes="40px"
                           className="object-cover"
                         />
                      </div>
                    ))}
                  </div>
                  <div className="text-sm">
                    <span className="font-bold block">Support Team</span>
                    <span className="text-muted-foreground">Available Mon-Fri, 9am-6pm</span>
                  </div>
                </div>
                <div className={cn(
                  "flex items-center gap-2 text-sm font-medium px-3 py-1.5 rounded-full w-fit",
                  isSupportOnline 
                    ? "text-emerald-600 bg-emerald-50" 
                    : "text-red-600 bg-red-50"
                )}>
                  <div className={cn(
                    "w-2 h-2 rounded-full",
                    isSupportOnline ? "bg-emerald-500 animate-pulse" : "bg-red-500"
                  )} />
                  {isSupportOnline ? 'Online now' : 'Currently Offline'}
                </div>
              </div>
            </motion.div>

            {/* Right Column: Form */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="bg-white rounded-2xl border border-foreground/5 shadow-[0_0_50px_-12px_rgba(53,140,127,0.15)] p-8 md:p-12 relative overflow-hidden">
                <CallbackForm source="contact-page" />
              </div>
            </motion.div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
